import { FC } from "react";

import { ErrorNotice } from "./ErrorNotice/ErrorNotice";

import { getOrganizationErrorNotice, isAwaitingReload } from "../helpers/getOrganizationErrorNotice";
import { getStarterChipGroup } from "../helpers/starterChips";
import { useStarterChips } from "../helpers/useStarterChips";
import type { OrganizationAssistantChat } from "../helpers/useOrganizationAssistant";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useChatWidgetRoute } from "@/components/ChatWidgetVisibilityManager/helpers/useChatWidgetRoute";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Chat } from "@/features/Assistant/Chat/Chat";
import type { AssistantChatViewProps } from "@/features/Assistant/helpers/types";
import { useAssistantUnavailableText } from "@/features/Assistant/helpers/useAssistantUnavailableText";

export const ChatView: FC<AssistantChatViewProps<OrganizationAssistantChat>> = ({
  chat,
  onStartNew,
  onReload,
  onMessageSent,
}) => {
  const { t } = useTranslate();
  const { pathWithoutLocale } = useChatWidgetRoute();
  const isServiceEntryPage = getStarterChipGroup(pathWithoutLocale) === "serviceEntry";
  const suggestions = useStarterChips();
  const notice = getOrganizationErrorNotice({ errorCode: chat.errorCode, messages: chat.messages });
  const unavailableText = useAssistantUnavailableText(
    trpc.getAiAvailability.useQuery({ feature: "organization-assistant" }),
    chat.errorCode === "rate_limited"
  );
  return (
    <Chat
      {...chat}
      isComposerDisabled={chat.isComposerDisabled || isAwaitingReload(notice) || Boolean(unavailableText)}
      suggestions={suggestions}
      welcome={
        isServiceEntryPage
          ? {
              title: t("dashboard", "formNew.serviceEntryB.assistant.welcomeTitle"),
              description: t("dashboard", "formNew.serviceEntryB.assistant.welcomeDescription"),
            }
          : undefined
      }
      errorNotice={
        unavailableText ? (
          <DashboardTypography role="alert" variant="body2" vibe="text-danger-intense" sx={{ pb: 1 }}>
            {unavailableText}
          </DashboardTypography>
        ) : notice && notice !== "generic" ? (
          <ErrorNotice notice={notice} onStartNew={onStartNew} onReload={onReload} />
        ) : undefined
      }
      onStartNew={onStartNew}
      onMessageSent={onMessageSent}
    />
  );
};
